import axiosInstance from "../config/axios.config";
import {
  extractPrintableHtml,
  openPrintWindow,
} from "../helpers/printDocument";

export const nurseCreatePharmacyOrderApi = (payload) => {
  return axiosInstance.post(`pharmacy/orders/nurse/create/`, payload);
};

export const getNursePharmacyOrdersApi = (params) => {
  return axiosInstance.get(`pharmacy/orders/nurse/?${params}`);
};

export const addPharmacyOrderApi = (payload) => {
  return axiosInstance.post(`pharmacy/orders/`, payload);
};

export const getPharmacyOrdersListApi = (params) => {
  return axiosInstance.get(`pharmacy/orders/?${params}`);
};

export const getPharmacyOrderApi = (id) => {
  return axiosInstance.get(`pharmacy/orders/${id}/`);
};

export const updatePharmacyOrderApi = (id, payload) => {
  return axiosInstance.patch(`pharmacy/orders/${id}/`, payload);
};


export const cancelPharmacyOrderApi = (id) => {
  return axiosInstance.delete(`pharmacy/orders/${id}/`);
};

/**
 * The whole order at once - the sale and its lines - replacing what is on the
 * server, rather than a line at a time through the items endpoints.
 */
export const editPharmacyOrderApi = (id, payload) => {
  return axiosInstance.put(`pharmacy/orders/${id}/edit/`, payload);
};

export const addPharmacyOrderItemApi = (orderId, payload) => {
  return axiosInstance.post(`pharmacy/orders/${orderId}/items/`, payload);
};

export const updatePharmacyOrderItemApi = (itemId, payload) => {
  return axiosInstance.patch(`pharmacy/orders/items/${itemId}/`, payload);
};

export const deletePharmacyOrderItemApi = (itemId) => {
  return axiosInstance.delete(`pharmacy/orders/items/${itemId}/`);
};

/*
 * The order's own receipt - what was asked for, what went out and what it came
 * to, as a document rather than a screen.
 *
 * `share=print` hands back the receipt HTML for the browser's print dialog,
 * which is where "download" ends up too: the dialog's own Save as PDF. The
 * endpoint's other branch, `share=download`, renders the PDF server-side and
 * returns a link to it, so a payload that turns out to be a URL is opened
 * rather than printed.
 */
export const pharmacyOrderReceiptApi = (orderId, params) => {
  return axiosInstance.post(`pharmacy/orders/${orderId}/receipt/?${params}`);
};

/*
 * Until the pharmacy has dispensed against an order there is no receipt to
 * draw - a pending order is a request, and a cancelled one never went out.
 */
const RECEIPT_STATUSES = ["partially_dispensed", "dispensed", "completed"];

export const canPrintOrderReceipt = (order) =>
  Boolean(order) &&
  (RECEIPT_STATUSES.includes(order.status) || Boolean(order.bill || order.bill_id));

export const printPharmacyOrderReceipt = async (orderId, type = "detailed") => {
  const resp = await pharmacyOrderReceiptApi(
    orderId,
    `share=print&type=${type}`
  );

  const payload = resp?.data?.data ?? resp?.data;
  if (typeof payload === "string" && /^https?:\/\//i.test(payload.trim())) {
    window.open(payload.trim(), "_blank", "noopener,noreferrer");
    return;
  }

  const html = extractPrintableHtml(resp?.data);
  if (!html) {
    throw new Error(
      resp?.data?.message || "No printable receipt was returned for this order"
    );
  }
  openPrintWindow(html);
};

export const holdPharmacyOrderApi = (orderId) => {
  return axiosInstance.post(`pharmacy/orders/${orderId}/hold/`, {});
};

export const dispensePharmacyOrderApi = (orderId, lines) => {
  return axiosInstance.post(`pharmacy/orders/${orderId}/dispense/`, { lines });
};

export const getPharmacyOrderDispensesApi = (orderId) => {
  return axiosInstance.get(`pharmacy/orders/${orderId}/dispenses/`);
};

export const suggestCounterSaleApi = (params) => {
  return axiosInstance.get(`pharmacy/search/counter-sale/?${params}`);
};

export const searchPharmacyPatientsApi = (params) => {
  return axiosInstance.get(`pharmacy/search/patients/?${params}`);
};

/*
 * Editing one line of a completed order.
 *
 * Before an order is submitted its lines are written with the three calls
 * above; once it is completed they are the server's record of stock that has
 * moved and money that has been billed, so they go through these instead.
 *
 * `batches` is the line's whole dispensed allocation rather than a change to
 * it - the server moves stock to or from each batch by the difference, and
 * rebuilds the order's bill lines after every call. Removing a line marks it
 * Removed rather than deleting it, and returns its stock.
 *
 * Refused (400) where the bill is cancelled, a sales return has been raised,
 * the order would be left with nothing dispensed, or the new bill total would
 * fall below what has already been paid.
 */
export const addPharmacyOrderEditItemApi = (orderId, payload) => {
  return axiosInstance.post(`pharmacy/orders/${orderId}/edit/items/`, payload);
};

export const updatePharmacyOrderEditItemApi = (orderId, itemId, payload) => {
  return axiosInstance.patch(`pharmacy/orders/${orderId}/edit/items/${itemId}/`, payload);
};

export const removePharmacyOrderEditItemApi = (orderId, itemId, payload) => {
  return axiosInstance.delete(
    `pharmacy/orders/${orderId}/edit/items/${itemId}/`,
    payload ? { data: payload } : undefined
  );
};
